import { demoProfile, mockUsers } from "./users";

type MockOrderItem = {
  id: string;
  name: string;
  price: number;
  quantity: number;
};

type MockOrder = {
  id: string;
  vendorId: string;
  vendorName: string;
  vendorDescription?: string | null;
  customerId: string;
  customerName?: string | null;
  totalAmount: number;
  status: string;
  deliveryType?: "delivery" | "takeout" | null;
  deliveryLocation?: string | null;
  description?: string | null;
  items: MockOrderItem[];
  createdAt: string;
  transactionId?: string | null;
  customer: {
    id: string | null;
    name: string | null;
    phone?: string | null;
  };
};

const initialOrders: MockOrder[] = [
  {
    id: "order-demo-1",
    vendorId: demoProfile.id,
    vendorName: demoProfile.businessName || demoProfile.name,
    vendorDescription: "Gifts and blooms delivered with care.",
    customerId: mockUsers.friend.id,
    customerName: mockUsers.friend.name,
    totalAmount: 48000,
    status: "PENDING",
    deliveryType: "delivery",
    deliveryLocation: "Victoria Island, Lagos",
    description: "Relaxing bouquet for a Valentine surprise.",
    items: [
      { id: "cat-flowers", name: "Valentine flowers", quantity: 1, price: 18000 },
      {
        id: "lola-gift-hamper",
        name: "Curated Gift Hamper",
        quantity: 1,
        price: 30000,
      },
    ],
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
    transactionId: "tx-demo-order-1",
    customer: {
      id: mockUsers.friend.id,
      name: mockUsers.friend.name,
      phone: mockUsers.friend.phone,
    },
  },
  {
    id: "order-demo-2",
    vendorId: demoProfile.id,
    vendorName: demoProfile.businessName || demoProfile.name,
    vendorDescription: "Handcrafted boxes and florals for special deliveries.",
    customerId: mockUsers.pendingOutgoing.id,
    customerName: mockUsers.pendingOutgoing.name,
    totalAmount: 22000,
    status: "PAID",
    deliveryType: "takeout",
    deliveryLocation: null,
    description: "Pickup bouquet for office celebration.",
    items: [
      {
        id: "cat-bouquet",
        name: "Valentines bouquet",
        quantity: 1,
        price: 22000,
      },
    ],
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
    transactionId: null,
    customer: {
      id: mockUsers.pendingOutgoing.id,
      name: mockUsers.pendingOutgoing.name,
      phone: mockUsers.pendingOutgoing.phone,
    },
  },
];

let orderStore: MockOrder[] = [...initialOrders];

const normalizePath = (path: string) => {
  if (path.startsWith("http")) {
    try {
      const url = new URL(path);
      return url.pathname + url.search;
    } catch {
      return path;
    }
  }
  return path;
};

const parseBody = (init?: RequestInit) => {
  try {
    if (init?.body && typeof init.body === "string") {
      return JSON.parse(init.body);
    }
  } catch {
    /* ignore */
  }
  return {};
};

const getHeaderUserId = (headers: Record<string, string> | undefined) => {
  if (!headers) return null;
  return (
    headers["x-user-id"] ||
    headers["X-User-Id"] ||
    headers["x-user-id".toLowerCase()] ||
    headers["X-USER-ID"]
  ) || null;
};

const findUserFixture = (id?: string) => {
  if (!id) return null;
  const fixtures = Object.values(mockUsers);
  return fixtures.find((user) => user.id === id) || null;
};

export async function handle(path: string, init?: RequestInit) {
  const normalized = normalizePath(path);
  const method = (init?.method || "GET").toUpperCase();
  const url = new URL(normalized, "http://mock.local");
  const pathname = url.pathname.replace(/^\/api/, "");
  const headers =
    (init?.headers as Record<string, string> | undefined) || {};
  const headerUserId = getHeaderUserId(headers);
  const vendorId = headerUserId || demoProfile.id;
  const customerFixture = findUserFixture(headerUserId) || mockUsers.demo;
  const customerId = customerFixture?.id || mockUsers.demo.id;
  const customerName =
    customerFixture?.name || "Valued customer";
  const customerPhone = customerFixture?.phone || null;

  if (pathname === "/orders" && method === "GET") {
    const vendorOrders = orderStore.filter((order) => order.vendorId === vendorId);
    return { orders: vendorOrders };
  }

  if (pathname === "/orders" && method === "POST") {
    const body = parseBody(init);
    const rawItems = Array.isArray(body.items) ? body.items : [];
    const items = rawItems.map((item, index) => ({
      id:
        typeof item?.id === "string"
          ? item.id
          : `order-item-${Date.now()}-${index}`,
      name: item?.name || `Item ${index + 1}`,
      price:
        typeof item?.price === "number"
          ? item.price
          : Number(item?.price || 0),
      quantity:
        typeof item?.quantity === "number" ? item.quantity : Number(item?.quantity || 1),
    }));
    const totalAmount =
      typeof body.totalAmount === "number"
        ? body.totalAmount
        : items.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0,
          );
    const deliveryOption = body.deliveryOption || {};
    const deliveryLocation = typeof deliveryOption.location === "string"
      ? deliveryOption.location.trim() || null
      : null;

    const newOrder: MockOrder = {
      id: `order-${Date.now()}`,
      vendorId: body.vendorId || vendorId,
      vendorName:
        body.vendorName ||
        demoProfile.businessName ||
        demoProfile.name,
      vendorDescription:
        body.vendorDescription || "Trusted seller accepting Bankdrop orders.",
      customerId,
      customerName: body.customerName || customerName,
      totalAmount: Number(totalAmount) || 0,
      status: "PENDING",
      deliveryType:
        deliveryOption.type === "delivery"
          ? "delivery"
          : deliveryOption.type === "takeout"
            ? "takeout"
            : null,
      deliveryLocation,
      description: body.description?.trim() || null,
      items,
      createdAt: new Date().toISOString(),
      transactionId:
        typeof body.transactionId === "string" ? body.transactionId : null,
      customer: {
        id: customerId,
        name: customerName,
        phone: customerPhone,
      },
    };

    orderStore = [newOrder, ...orderStore];
    return { order: newOrder };
  }

  const actionMatch = pathname.match(
    /^\/orders\/([^/]+)\/(confirm-payment|decline-payment)$/,
  );
  if (actionMatch && method === "POST") {
    const [, orderId, action] = actionMatch;
    const orderIndex = orderStore.findIndex((order) => order.id === orderId);
    if (orderIndex === -1) {
      return new Response(
        JSON.stringify({ error: "Order not found" }),
        { status: 404, headers: { "Content-Type": "application/json" } },
      );
    }
    const nextStatus = action === "confirm-payment" ? "PAID" : "DECLINED";
    const updatedOrder = {
      ...orderStore[orderIndex],
      status: nextStatus,
    };
    orderStore = [
      ...orderStore.slice(0, orderIndex),
      updatedOrder,
      ...orderStore.slice(orderIndex + 1),
    ];
    return { order: updatedOrder };
  }

  const linkMatch = pathname.match(/^\/orders\/([^/]+)\/link-transaction$/);
  if (linkMatch && method === "POST") {
    const [, orderId] = linkMatch;
    const orderIndex = orderStore.findIndex((order) => order.id === orderId);
    if (orderIndex === -1) {
      return new Response(
        JSON.stringify({ error: "Order not found" }),
        { status: 404, headers: { "Content-Type": "application/json" } },
      );
    }
    const body = parseBody(init);
    if (!body.transactionId) {
      return new Response(
        JSON.stringify({ error: "Transaction id required" }),
        { status: 400, headers: { "Content-Type": "application/json" } },
      );
    }
    const updatedOrder = {
      ...orderStore[orderIndex],
      transactionId: body.transactionId,
    };
    orderStore = [
      ...orderStore.slice(0, orderIndex),
      updatedOrder,
      ...orderStore.slice(orderIndex + 1),
    ];
    return { order: updatedOrder };
  }

  return null;
}
